import React, { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Text, Textarea, Link, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useApiRequest } from '../hooks/useApiRequest';
import { useFiscalEndReviewPageStyles } from '../styles/FiscalEndReviewPage.styles';
import type { Note } from '../models/partnerReview';

export interface NotesEditorHandle {
  flush: () => void;
  clearSystemPrefix: () => void;
}

export const NotesEditor = forwardRef<NotesEditorHandle, {
  mpnId: string;
  initialBody: string;
  initialSystemPrefix: string | null;
  isLoading: boolean;
  onNoteSaved: (mpnId: string, body: string) => void;
  onNotesHistoryClick: () => void;
}>(({ mpnId, initialBody, initialSystemPrefix, isLoading, onNoteSaved, onNotesHistoryClick }, ref) => {
  const styles = useFiscalEndReviewPageStyles();
  const [body, setBody] = useState(initialBody);
  const [systemPrefix, setSystemPrefix] = useState<string | null>(initialSystemPrefix);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveTime, setSaveTime] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ mpnId: string; body: string } | null>(null);
  const { error: saveError, execute: executeSave } = useApiRequest<Note>();

  useEffect(() => {
    setBody(initialBody);
    setSystemPrefix(initialSystemPrefix);
    setSaveStatus('idle');
  }, [initialBody, initialSystemPrefix, mpnId]);

  useEffect(() => {
    if (saveError) setSaveStatus('error');
  }, [saveError]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      executeSave(async (api) => {
        const result = await api.put<Note>('/Notes', { mpnId: pending.mpnId, body: pending.body });
        return result;
      });
    }
  }, [executeSave]);

  const clearSystemPrefix = useCallback(() => {
    setSystemPrefix(null);
  }, []);

  useImperativeHandle(ref, () => ({ flush, clearSystemPrefix }), [flush, clearSystemPrefix]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const onChange = useCallback((_e: React.ChangeEvent<HTMLTextAreaElement>, data: { value: string }) => {
    setBody(data.value);
    setSaveStatus('idle');

    if (timerRef.current) clearTimeout(timerRef.current);

    pendingRef.current = { mpnId, body: data.value };

    timerRef.current = setTimeout(() => {
      pendingRef.current = null;
      setSaveStatus('saving');
      executeSave(async (api) => {
        const result = await api.put<Note>('/Notes', { mpnId, body: data.value });
        setSaveStatus('saved');
        setSaveTime(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
        onNoteSaved(mpnId, data.value);
        return result;
      });
    }, 1000);
  }, [mpnId, executeSave, onNoteSaved]);

  return (
    <>
      <hr className={styles.drawerDivider} />
      <Text className={styles.drawerSectionTitle} block>Notes</Text>
      {isLoading ? (
        <Skeleton>
          <SkeletonItem style={{ width: '100%', height: '80px' }} />
        </Skeleton>
      ) : (
        <>
          {systemPrefix && (
            <Text
              size={200}
              style={{ display: 'block', fontStyle: 'italic', opacity: 0.7, marginBottom: '6px' }}
            >
              {systemPrefix}
            </Text>
          )}
          <Textarea
            placeholder="Add a note about this partner..."
            resize="vertical"
            style={{ width: '100%' }}
            textarea={{ style: { minHeight: '80px' } }}
            value={body}
            onChange={onChange}
          />
        </>
      )}
      <div className={styles.notesSaveRow}>
        <Text size={200}>
          Click here to see{' '}
          <Link inline onClick={onNotesHistoryClick}>Notes history</Link>
        </Text>
        {saveStatus === 'saving' && (
          <Text size={200} className={styles.mutedText}>Saving...</Text>
        )}
        {saveStatus === 'saved' && (
          <Text size={200} className={styles.mutedText}>Auto-saved at {saveTime}</Text>
        )}
        {saveStatus === 'error' && (
          <Text size={200} className={styles.errorText}>Auto-save failed</Text>
        )}
      </div>
    </>
  );
});
